import { useEffect, useRef, useState } from 'react';
import { attendanceLocationSchema, PUBLIC_ATTENDANCE_LOCATION_MAX_AGE_MS } from 'shared-models/browser';
import type { AttendanceLocation } from 'shared-models/browser';
import { tenantsService, type TenantView } from '../../services/tenants/tenants.service';

export type AttendanceLocationDraft = { latitude: string; longitude: string };

function parseDraft(draft: AttendanceLocationDraft): AttendanceLocation | null {
  if (!draft.latitude.trim() && !draft.longitude.trim()) return null;
  const parsed = attendanceLocationSchema.safeParse({ latitude: Number(draft.latitude), longitude: Number(draft.longitude) });
  if (!draft.latitude.trim() || !draft.longitude.trim() || !parsed.success) throw new Error('יש להזין קו רוחב וקו אורך תקינים');
  return parsed.data;
}

export function useTenantAttendanceLocation() {
  const [drafts, setDrafts] = useState<Record<number, AttendanceLocationDraft>>({});
  const [locatingTenantId, setLocatingTenantId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lookup = useRef<number | null>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const getDraft = (tenant: TenantView): AttendanceLocationDraft => drafts[tenant.id] ?? {
    latitude: tenant.publicAttendanceLocation ? String(tenant.publicAttendanceLocation.latitude) : '',
    longitude: tenant.publicAttendanceLocation ? String(tenant.publicAttendanceLocation.longitude) : '',
  };
  const setDraft = (tenantId: number, draft: AttendanceLocationDraft) => setDrafts((previous) => ({ ...previous, [tenantId]: draft }));
  const isChanged = (tenant: TenantView) => {
    if (!drafts[tenant.id]) return false;
    try {
      const location = parseDraft(getDraft(tenant));
      return location?.latitude !== tenant.publicAttendanceLocation?.latitude || location?.longitude !== tenant.publicAttendanceLocation?.longitude;
    } catch { return true; }
  };
  const save = async (tenant: TenantView) => {
    if (!isChanged(tenant)) return;
    await tenantsService.updatePublicAttendanceLocation(tenant.id, { publicAttendanceLocation: parseDraft(getDraft(tenant)) });
    setDrafts((previous) => { const next = { ...previous }; delete next[tenant.id]; return next; });
  };
  const useCurrentLocation = async (tenant: TenantView) => {
    if (lookup.current !== null) return;
    lookup.current = tenant.id; setLocatingTenantId(tenant.id); setError(null);
    try {
      if (!navigator.geolocation) throw new Error();
      const position = await new Promise<GeolocationPosition>((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }));
      const location = attendanceLocationSchema.parse({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      if (Math.abs(Date.now() - position.timestamp) > PUBLIC_ATTENDANCE_LOCATION_MAX_AGE_MS) throw new Error();
      if (mounted.current) setDraft(tenant.id, { latitude: String(location.latitude), longitude: String(location.longitude) });
    } catch { if (mounted.current) setError('לא ניתן לקבל מיקום. יש לאפשר גישה למיקום ולנסות שוב'); }
    finally { lookup.current = null; if (mounted.current) setLocatingTenantId(null); }
  };
  return { getDraft, setDraft, isChanged, save, useCurrentLocation, locatingTenantId, error };
}

export type TenantAttendanceLocationController = ReturnType<typeof useTenantAttendanceLocation>;